import React, { useContext, useEffect, useState } from 'react'
import { Box } from '@mui/material'

import LanguageContext from "../../../contexts/LanguageContext"
import { useTexte } from "../../../contexts/FormeAdresseContext"
import UserContext from "../../../contexts/UserContext"
import { getCurrentUser } from "../../../utils/auth"
import { auMoinsUneCategorieCochee } from "../../../utils/ponderationModule"
import SuperAdminContext from "../../../contexts/SuperAdminContext"

import { TopBar, BottomBar, CenterContainer, BlocIllustre, LARGEUR_LECTURE } from "./FormatsHelpers"
import EditableField from "./EditableField"
import EditableButton from "./EditableButton"
import { pourcentLisible } from "./FormatDiag"

import EditorPopover from "../../ui/Admin/Formations/EditorPopover"
import FinDeModuleHelper, { useRelirePonderation } from "../../ui/Admin/Formations/FormatHelpers/FinDeModuleHelper"

/* Le score que la plateforme LIT (Mon résultat, certificat), et le dessin du
   certificat : importes, jamais recopies ni modifies. */
import { SEUIL_SCORE_CERTIFICAT, scoreGlobalDuModule, scoreDuModulePourCertificat, scoreSuffisantPourCertificat } from "../Certificat/moduleTermine"
import { contourRosette, contourCoche, COULEURS } from "../Certificat/modeleCertificat"

/* Icone et quadrillage de la carte de score du Diagnostic (FormatDiag). */
import iconeScoreGlobal from "../../../images/diagnostic-score-global.svg"
import fondCarteScore from "../../../images/diagnostic-carte-fond.svg"

/* ⛔ FORMAT « FIN DE MODULE » (07/10/2026, fiche Notion de Nawal, partie B).

   UNE SLIDE, que Nawal place ou elle veut (en general la derniere du module).
   Ce n'est PAS l'ecran terminal `FinDeFormation` (« Fin de formation » dans
   l'editeur), qui reste tel quel, ni le Diagnostic, qui ne change pas.

   ⛔ MISE EN PAGE : LE BLOC DU HAUT DU DIAGNOSTIC, A L'IDENTIQUE (retour de
   Nawal du 07/10 : « ni les dimensions ni les positions du Diagnostic »). Le
   rendu ci-dessous recopie l'arbre et les `sx` de FormatDiag (colonne de
   gauche, carte de score 4:1442) ; toute valeur changee la-bas doit l'etre ici.
   Les positions sont verifiees au pixel contre le Diagnostic, aux trois
   largeurs (banc de photos du 07/10). Ce qui change, et seulement ca :
     - a gauche, le TITRE (`answers.titreResultat`, sinon « Votre / Ton
       résultat pour ce module ») et le TEXTE du resultat (`answers.textes`), a
       la place du titre et du texte de palier du Diagnostic ;
     - dans la carte, la ROSETTE du certificat a la place de l'icone (4:1445),
       meme boite, et « Validé » / « Pas encore validé » a la place de « Score
       global » ; le score dessous, comme dans le Diagnostic ;
     - rien sous le bloc : il se CENTRE EN HAUTEUR (v3, demande de Nawal),
       les x et les tailles restant ceux du Diagnostic.

   LE SCORE : le SCORE DU MODULE (glbMaster), valide a partir de
   SEUIL_SCORE_CERTIFICAT (50 compris depuis le 07/10/2026, arrondi comme affiche). La valeur est celle de
   la plateforme, et elle n'est calculee nulle part ici :
     - module deja fait : son `resultFin` enregistre ;
     - sinon : le calcul de fin de module, `getFormationResults` (UserContext),
       celui-la meme que l'ecran de fin enregistrera en `resultFin` ;
   puis LU par les fonctions du certificat (Certificat/moduleTermine.js).

   ⛔ PAS DE SCORE DE MODULE (rien de coche « oui ») : la carte du Diagnostic
   telle quelle (son icone, « Score global ») avec ce que « Mon résultat »
   montre dans ce cas, le score global ; pas de texte de resultat.

   LES TEXTES : « validé », vide par defaut ; « pas encore validé », tant que
   Nawal n'a rien ecrit, l'annonce du rattrapage (« Un module de rattrapage
   vous / te sera proposé. »). ⛔ Jamais de consigne du type « refais les
   videos » (Nawal, 07/10).

   CALCUL REEL SEULEMENT DANS LE PARCOURS (apprenant ou essai depuis l'admin) :
   quand le module affiche est celui du contexte. Dans l'editeur et les
   vignettes de l'admin, on montre un EXEMPLE.

   ⛔ PD-10 : un candidat THE SCAN ne voit aucun resultat. Rien n'est calcule,
   l'ecran neutre du Diagnostic le remplace.

   « Suivant » : comme un ecran sans reponse (Texte, Titre) : `isDone`, rien
   d'autre ne part. */

export const ETAT_VALIDE = "valide"
export const ETAT_PAS_ENCORE_VALIDE = "pasEncoreValide"
export const ETAT_SANS_SCORE = "sansScore"

/* Valeurs d'EXEMPLE, hors parcours (editeur, vignettes de l'admin) */
export const EXEMPLES = { [ETAT_VALIDE]: 75, [ETAT_PAS_ENCORE_VALIDE]: 40, [ETAT_SANS_SCORE]: 62 }

/* Le `resultFin` du module : enregistre s'il est fait, sinon celui que l'ecran
   de fin enregistrera (meme calcul, meme mise en JSON : FinDeFormation). */
export const resultFinDuModule = (module, getFormationResults) => {
  if (module?.isDone === true && module?.resultFin) return module.resultFin
  const r = typeof getFormationResults === "function" ? getFormationResults() : null
  return r?.categories ? JSON.stringify(r.categories) : null
}

/* `resultFin` -> { etat, score } : lu par les fonctions du certificat. */
export const lectureDuResultat = (resultFin) => {
  const lecture = { resultFin }
  const scoreModule = scoreDuModulePourCertificat(lecture)
  const affiche = scoreGlobalDuModule(lecture)
  return {
    etat: scoreModule === null ? ETAT_SANS_SCORE : (scoreSuffisantPourCertificat(lecture) ? ETAT_VALIDE : ETAT_PAS_ENCORE_VALIDE),
    /* jamais « NaN » ni vide : 0, comme « Mon résultat » et le Diagnostic */
    score: affiche === null ? 0 : affiche,
  }
}

/* `answers` lisible (meme garde que les titres du Diagnostic : doublement
   encode, ce serait une chaine) */
const answersDe = (format) => (format?.answers && typeof format.answers === "object") ? format.answers : {}
const rempli = (v) => typeof v === "string" && v.trim() !== ""

/* Le texte ECRIT par Nawal pour cet etat (`answers.textes.valide` ou
   `.pasEncoreValide`), vide sinon. */
export const texteDuResultat = (answers, etat) => {
  const a = (answers && typeof answers === "object") ? answers : {}
  const textes = (a.textes && typeof a.textes === "object") ? a.textes : {}
  const v = textes[etat]
  return typeof v === "string" ? v : ""
}

/* LA ROSETTE DU CERTIFICAT, redessinee a l'ecran avec ses propres traces
   (`contourRosette`, `contourCoche`, modeleCertificat.js). Validee : aplat
   orange, coche blanche. Pas encore : contour fin gris #8A8480 (COULEURS.
   texteMuet du certificat), sans coche ni remplissage, 1,5 px a toute taille
   (`non-scaling-stroke`). Decorative : le libelle a cote la nomme. */
const CENTRE = 50, RAYON = 44
const trace = (points) => "M" + points.map(([x, y]) => x.toFixed(3) + " " + y.toFixed(3)).join("L") + "Z"
export const RosetteValidation = ({ validee, largeur, hauteur }) => (
  <Box
    component="svg"
    viewBox="0 0 100 100"
    aria-hidden="true"
    data-rosette={validee ? "validee" : "pas-encore-validee"}
    sx={{ width: largeur, height: hauteur, display:"block", flexShrink:0, overflow:"visible" }}
  >
    {validee
      ? <>
          <path d={trace(contourRosette(CENTRE, CENTRE, RAYON))} fill={COULEURS.orange} />
          <path d={trace(contourCoche(CENTRE, CENTRE, RAYON))} fill={COULEURS.blanc} />
        </>
      : <path d={trace(contourRosette(CENTRE, CENTRE, RAYON))} fill="none" stroke={COULEURS.texteMuet} strokeWidth="1.5" vectorEffect="non-scaling-stroke" />}
  </Box>
)

/* ⛔ V3 (07/10/2026, Nawal sur l'apercu) : LE BLOC SE CENTRE EN HAUTEUR,
   entre l'en-tete et la barre du bas, puisqu'il n'y a rien dessous (en v2 il
   paraissait colle en haut). L'horizontal ne change pas.
   Le centrage se fait par des marges AUTOMATIQUES : s'il ne tient pas
   (fenetre basse), elles tombent a 0, le bloc repart du haut et la zone
   defile, sans rien couper.
   La zone (CenterContainer) n'a pas la meme marge en haut et en bas : en haut
   `RETRAIT_HAUT` (passe ci-dessous), en bas 0 en Mobile et 40 ailleurs (ses
   valeurs par defaut). On ajoute la difference sous le bloc, pour qu'il soit
   centre entre l'en-tete et la barre du bas, et non dans cette zone
   dissymetrique. */
const RETRAIT_HAUT = [48, 50, 72, 72]
const RETRAIT_BAS_DE_LA_ZONE = [0, 40, 40, 40]
const COMPENSATION_BAS = RETRAIT_HAUT.map((h, i) => `${h - RETRAIT_BAS_DE_LA_ZONE[i]}px`)

/* Les deux textes de Nawal, vides, pour une slide qui n'en aurait pas la
   structure (EditableField ecrit dans `answers.textes.<etat>`). */
const TEXTES_VIDES = { valide: "", pasEncoreValide: "" }

const FormatFinDeModule = ({ actFormation, actIndex, format, setViewPlanCours, viewPlanCours, onlyContent, isEditor, sequenceSubMenu }) => {
  const { actLanguage, languageValues } = useContext(LanguageContext)
  /* Tutoiement / vouvoiement selon la cible du module (utils/formeAdresse.js) */
  const t = useTexte()
  const { manageFormatResponse, manageSlide, getFormationResults, actFormation: moduleDuParcours } = useContext(UserContext)

  const vueCandidat = getCurrentUser()?.type === "theScanCandidat" && isEditor !== true
  const parcours = isEditor !== true && !vueCandidat && !!actFormation && actFormation === moduleDuParcours

  /* Hors parcours : un EXEMPLE. Par defaut, celui que la ponderation du module
     annonce ; dans l'editeur, Nawal passe d'un etat a l'autre (panneau). */
  const [apercu, setApercu] = useState(null)
  const etatExemple = apercu || (auMoinsUneCategorieCochee(actFormation) ? ETAT_VALIDE : ETAT_SANS_SCORE)

  /* EDITEUR : la ponderation relue sur le module enregistre (le formulaire a pu
     la changer), et la structure des textes posee si elle manque. */
  const admin = useContext(SuperAdminContext)
  useRelirePonderation(actFormation?.id, isEditor === true)
  const answers = answersDe(format)
  const textesPresents = answers.textes && typeof answers.textes === "object"
  useEffect(() => {
    if (isEditor !== true || textesPresents || !admin?.onSequenceChange) return
    admin.onSequenceChange("answers", { ...answersDe(format), textes: { ...TEXTES_VIDES } }, admin.adminPlayerIndexes)
  }, [isEditor, textesPresents])

  const { etat, score } = parcours
    ? lectureDuResultat(resultFinDuModule(actFormation, getFormationResults))
    : { etat: etatExemple, score: EXEMPLES[etatExemple] }
  const avecRosette = etat === ETAT_VALIDE || etat === ETAT_PAS_ENCORE_VALIDE

  /* Le titre : celui de Nawal, sinon le defaut (tu ou vous selon la cible). */
  const titre = rempli(answers.titreResultat) ? answers.titreResultat : (t(languageValues?.formation?.finModuleTitre) || "")
  /* Le texte : celui de Nawal ; « pas encore validé » sans texte : l'annonce
     du rattrapage. Dans l'editeur, le champ montre ce que Nawal a ecrit, et le
     defaut en texte indicatif. */
  const ecrit = avecRosette ? texteDuResultat(answers, etat) : ""
  const defautPasEncore = t(languageValues?.formation?.finModulePasEncoreValideDefaut) || ""
  const texte = !avecRosette ? "" : rempli(ecrit) ? ecrit : (etat === ETAT_PAS_ENCORE_VALIDE ? defautPasEncore : "")
  const indicatif = isEditor && avecRosette ? {
    minHeight:"1em",
    "&:empty::before": {
      content: JSON.stringify(etat === ETAT_PAS_ENCORE_VALIDE
        ? defautPasEncore
        : (t(languageValues?.formation?.finModuleTexteSiValide) || "").replace("{seuil}", pourcentLisible(SEUIL_SCORE_CERTIFICAT, actLanguage))),
      color:"rgba(10,10,11,0.35)",
    },
  } : {}

  const continuer = () => {
    manageFormatResponse(format, [{ key: "isDone", value: true }])
    manageSlide(1)
  }

  return (
    <Box sx={{
      display:"flex",
      flexDirection:"column",
      /* comme le Diagnostic : la variable n'existe que dans le panneau d'apercu */
      height:onlyContent?"100%":"var(--hs-hauteur-ecran, 100vh)",
      containerType:"size",
      position:"static",
      overflow:"hidden",
    }}>
      {isEditor &&
        <EditorPopover open={sequenceSubMenu}>
          <FinDeModuleHelper
            actFormation={actFormation}
            format={format}
            apercu={etatExemple}
            setApercu={setApercu}
            etats={{ valide: ETAT_VALIDE, pasEncoreValide: ETAT_PAS_ENCORE_VALIDE, sansScore: ETAT_SANS_SCORE }}
            exemples={EXEMPLES}
          />
        </EditorPopover>
      }
      <TopBar
        actFormation={actFormation}
        actIndex={actIndex}
        setViewPlanCours={setViewPlanCours}
        viewPlanCours={viewPlanCours}
        onlyContent={onlyContent}
      />
      {/* Pastille en tete de colonne et haut de page : ceux du Diagnostic */}
      <CenterContainer
        actFormation={actFormation}
        actIndex={actIndex}
        onlyContent={onlyContent}
        cote={null}
        pastilleDansColonne
        pastilleEnTete={["20px", "30px", "30px", "30px"]}
        retraitHaut={RETRAIT_HAUT}
      >
        {({ pastille }) => (
        <BlocIllustre image={null} horsFlux={null} sx={{ flex:1, minHeight:0 }}>
        {/* PD-10 : l'ecran neutre du candidat, celui du Diagnostic */}
        {vueCandidat &&
          <Box sx={{ display:"flex", flexDirection:"column", alignItems:"flex-start", width:"100%", maxWidth: LARGEUR_LECTURE }}>
          {pastille}
          <Box sx={{
            width:"100%",
            maxWidth: LARGEUR_LECTURE,
            color:"text.black",
            fontSize:["20px", "24px", "26px", "28px"],
            lineHeight:1.3,
            textAlign:"left",
          }}>
            {t(languageValues?.formation?.merciPourVosReponses)}
          </Box>
          </Box>
        }
        {!vueCandidat &&
        /* ⚠️ A PARTIR D'ICI, L'ARBRE ET LES `sx` DE FormatDiag, dans le meme
           ordre (bloc du haut du Diagnostic). */
        <Box sx={{
          width:"100%",
          /* ⚠️ DIFFERENT DU DIAGNOSTIC (["unset", "unset", "unset", 1]
             la-bas, ou c'est la LISTE qui remplit la hauteur). Ici, rien sous
             le bloc : la colonne remplit la hauteur et le bloc s'y centre (v3,
             voir COMPENSATION_BAS). Sur une fenetre trop basse, la zone
             defile. */
          flex:[1, 1, 1, 1],
          minHeight:0,
          display:"flex",
          flexDirection:["column", "column", "column", "row"],
          gap:["20px", "40px", "40px", "60px"],
          overflow:"unset",
        }}>
          <Box sx={{
            /* remplit la hauteur a tous les paliers (v3 : le bloc s'y centre) */
            flex:[1, 1, 1, 1],
            minWidth:0,
            display:"flex",
            flexDirection:"column",
          }}>
            <Box data-fin-module sx={{
              width:"100%",
              maxWidth:["100%", "750px", "1320px", "1320px"],
              marginLeft:"auto",
              marginRight:"auto",
              flex:1,
              minHeight:0,
              display:"flex",
              flexDirection:"column",
              /* v3 : la difference des marges de la zone, sous le bloc */
              paddingBottom:COMPENSATION_BAS,
              boxSizing:"border-box",
            }}>
              {/* en-tete en deux colonnes (Diagnostic, 4:1433) */}
              <Box data-fin-module-bloc sx={{
                display:"flex",
                width:"100%",
                flexDirection:["column", "column", "column", "row"],
                alignItems:["stretch", "flex-start", "flex-start", "flex-end"],
                justifyContent:"space-between",
                gap:"40px",
                flexShrink:0,
                /* v3 : centre en hauteur s'il tient, en haut sinon */
                marginTop:"auto",
                marginBottom:"auto",
              }}>
              {/* colonne de gauche : pastille, titre, texte (4:1434) */}
              <Box sx={{
                display:"flex",
                flexDirection:"column",
                alignItems:"flex-start",
                width:"100%",
                maxWidth:["100%", "650px", "650px", "650px"],
                minWidth:0,
                alignSelf:"flex-start",
              }}>
              {pastille}
              {!!titre &&
                <Box data-fin-module-titre sx={{
                  /* le titre du Diagnostic : 42 (30 en Mobile), 4:1439, 4:1772 */
                  color:"text.black",
                  fontSize:["30px", "42px", "42px", "42px"],
                  textAlign:"left",
                  lineHeight:1.15,
                  textBox:"trim-both cap alphabetic",
                  marginBottom:["20px", "30px", "30px", "30px"],
                }}>
                  {titre}
                </Box>
              }
              {/* le texte du resultat, a la place du texte de palier
                  (4:1440, 4:1773) */}
              <EditableField
                key={etat}
                field={isEditor ? ecrit : texte}
                isEditor={isEditor === true && avecRosette}
                type={["answers", "textes", etat]}
                sx={{
                  color:"text.subTitle",
                  fontSize:["15px", "20px", "20px", "20px"],
                  textAlign:"left",
                  lineHeight:1.5,
                  textBox:"trim-both cap alphabetic",
                  ...indicatif,
                }}
              />
              </Box>
              {/* CARTE SCORE GLOBAL — celle du Diagnostic (4:1442), au pixel */}
              <Box data-fin-module-carte={etat} sx={{
                flexShrink:0,
                backgroundColor:"white",
                alignSelf:["stretch", "flex-start", "flex-start", "flex-end"],
                marginTop:0,
                border:"1px solid #E7E7E7",
                padding:["14px 12px 26px", "19px", "19px", "19px"],
                minWidth:["auto", "320px", "320px", "320px"],
                boxSizing:"border-box",
                display:"flex",
                flexDirection:"column",
                alignItems:"flex-start",
                position:"relative",
                overflow:"hidden",
              }}>
                <Box component="img" src={fondCarteScore} alt="" aria-hidden="true" sx={{
                  position:"absolute", right:"5px", bottom:"5px",
                  width:"172px",
                  height:"172px",
                  pointerEvents:"none", userSelect:"none",
                  display:"block",
                }} />
                <Box sx={{ display:"flex", alignItems:"center", gap:["12px", "10px", "10px", "10px"], alignSelf:"stretch", marginBottom:["28px", "27px", "27px", "27px"] }}>
                  <Box sx={{
                    flexShrink:0, width:["45px", "50px", "50px", "50px"], height:["45px", "50px", "50px", "50px"],
                    backgroundColor:"#FAF8F6", border:"0.5px solid #E6E6E7",
                    display:"flex", alignItems:"center", justifyContent:"center",
                  }}>
                    <Box sx={{
                      width:["35px", "40px", "40px", "40px"], height:["35px", "40px", "40px", "40px"], backgroundColor:"white",
                      boxShadow:"0px 2px 1.5px 0.5px rgba(149,160,178,0.16)",
                      display:"flex", alignItems:"center", justifyContent:"center",
                    }}>
                      {/* la rosette a la place exacte de l'icone 4:1445, meme
                          boite (21,58 x 22) ; sans score de module, l'icone */}
                      {avecRosette
                        ? <RosetteValidation validee={etat === ETAT_VALIDE} largeur="21.58px" hauteur="22px" />
                        : <Box component="img" src={iconeScoreGlobal} alt="" sx={{ width:"21.58px", height:"22px", display:"block" }} />}
                    </Box>
                  </Box>
                  <Box data-fin-module-libelle sx={{ color:"rgba(26,25,37,0.75)", fontSize:["16px", "18px", "18px", "18px"], letterSpacing:"-0.02em" }}>
                    {t(etat === ETAT_VALIDE ? languageValues?.formation?.finModuleValide
                      : etat === ETAT_PAS_ENCORE_VALIDE ? languageValues?.formation?.finModulePasEncoreValide
                      : languageValues?.formation?.scoreGlobal)}
                  </Box>
                </Box>
                <Box sx={{
                  display:"flex",
                  flexDirection:["row", "column", "column", "column"],
                  alignItems:["flex-end", "flex-start", "flex-start", "flex-start"],
                  gap:["60px", "25px", "25px", "25px"],
                  width:"100%",
                }}>
                  <Box sx={{ display:"flex", alignItems:"baseline", flexShrink:0, userSelect:"none" }}>
                    <Box data-fin-module-score sx={{ color:"#1A1925", fontSize:["52px", "62px", "62px", "62px"], lineHeight:"36px", letterSpacing:"-0.02em", textBox:"trim-both cap alphabetic" }}>
                      {score}
                    </Box>
                    <Box sx={{ fontSize:["26px", "32px", "32px", "32px"], lineHeight:"36px", color:"rgba(26,25,37,0.75)", letterSpacing:"-0.02em", textBox:"trim-both cap alphabetic",
                      marginLeft: actLanguage === 'en' ? [0, "-3.7px", "-3.7px", "-3.7px"] : 0 }}>
                      {/* l'espace fine du Diagnostic en francais, rien en anglais */}
                      {pourcentLisible("", actLanguage)}
                    </Box>
                  </Box>
                </Box>
              </Box>
              </Box>
            </Box>
          </Box>
        </Box>
        }
        </BlocIllustre>
        )}
      </CenterContainer>
      <BottomBar
        actFormation={actFormation}
        actIndex={actIndex}
        onlyContent={isEditor ? false : onlyContent}
        isEditor={isEditor}
        blanche
        buttons={
          <EditableButton
            isEditor={false}
            type="button"
            /* libelle calcule, comme le Diagnostic (PD-26) */
            field={languageValues?.next?.[actLanguage]}
            sx={{}}
            onClick={continuer}
          />
        }
      />
    </Box>
  )
}

export default FormatFinDeModule
